/**
 * RecommendationEditor.tsx — edit spec.recommendation:
 *   - productKey (the catalog family the recommender scores over)
 *   - filters (hard include/exclude conditions, JSON)
 *   - criteria rows with WEIGHT SLIDERS + direction + normalize + expr
 *   - slots (primary / alternative / budget) with rank + optional filter
 */

import { Plus, Trash2 } from 'lucide-react';
import type { SpecCriterion, SpecRecommendation, SpecSlot } from '../../../api/calculatorBuilder';
import { StudioPanel, field, btnAccent, label, btnGhostDanger } from './ui';

interface Props {
  recommendation: SpecRecommendation;
  onChange: (rec: SpecRecommendation) => void;
}

const SLOTS: SpecSlot['slot'][] = ['primary', 'alternative', 'budget'];

export default function RecommendationEditor({ recommendation, onChange }: Props) {
  const rec = recommendation ?? { productKey: '', filters: [], criteria: [], slots: [] };

  const setRec = (p: Partial<SpecRecommendation>) => onChange({ ...rec, ...p });

  // ── criteria ──
  const patchCrit = (i: number, p: Partial<SpecCriterion>) =>
    setRec({ criteria: rec.criteria.map((c, idx) => (idx === i ? { ...c, ...p } : c)) });
  const addCrit = () =>
    setRec({
      criteria: [
        ...rec.criteria,
        { key: `criterion_${rec.criteria.length + 1}`, weight: 1, expr: { op: 'const', value: 0 }, direction: 'higher', normalize: 'minmax' },
      ],
    });
  const removeCrit = (i: number) => setRec({ criteria: rec.criteria.filter((_, idx) => idx !== i) });

  // ── slots ──
  const patchSlot = (i: number, p: Partial<SpecSlot>) => setRec({ slots: rec.slots.map((s, idx) => (idx === i ? { ...s, ...p } : s)) });
  const addSlot = () => setRec({ slots: [...rec.slots, { slot: 'primary', rank: rec.slots.length + 1 }] });
  const removeSlot = (i: number) => setRec({ slots: rec.slots.filter((_, idx) => idx !== i) });

  return (
    <StudioPanel title="Recommendation" subtitle="Filter candidates, then score + rank them into slots.">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* productKey */}
        <div>
          <span style={label}>Product family key</span>
          <input
            style={{ ...field, fontFamily: 'ui-monospace, monospace', width: 260 }}
            value={rec.productKey}
            onChange={(e) => setRec({ productKey: e.target.value.trim() })}
            placeholder="ss_tank"
          />
        </div>

        {/* filters (JSON) */}
        <div>
          <span style={label}>Filters (array of conditions, JSON — hard include/exclude)</span>
          <textarea
            style={{ ...field, width: '100%', height: 84, padding: 10, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
            defaultValue={JSON.stringify(rec.filters ?? [], null, 2)}
            onBlur={(e) => {
              try {
                setRec({ filters: JSON.parse(e.target.value) });
              } catch {
                /* keep prior on invalid JSON */
              }
            }}
          />
        </div>

        {/* criteria with weight sliders */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ ...label, marginBottom: 0 }}>Scoring criteria</span>
            <button type="button" style={{ ...btnAccent, marginLeft: 'auto' }} onClick={addCrit}>
              <Plus size={14} /> Add criterion
            </button>
          </div>
          {rec.criteria.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-3, #6b7280)' }}>No criteria.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {rec.criteria.map((c, i) => (
              <div key={i} style={{ border: '1px solid var(--line, #e5e7eb)', borderRadius: 9, padding: 12 }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div>
                    <span style={label}>Key</span>
                    <input style={{ ...field, fontFamily: 'ui-monospace, monospace' }} value={c.key} onChange={(e) => patchCrit(i, { key: e.target.value.trim() })} />
                  </div>
                  <div>
                    <span style={label}>Direction</span>
                    <select style={field} value={c.direction} onChange={(e) => patchCrit(i, { direction: e.target.value as SpecCriterion['direction'] })}>
                      <option value="higher">higher is better</option>
                      <option value="lower">lower is better</option>
                    </select>
                  </div>
                  <div>
                    <span style={label}>Normalize</span>
                    <select style={field} value={c.normalize} onChange={(e) => patchCrit(i, { normalize: e.target.value as SpecCriterion['normalize'] })}>
                      <option value="minmax">min-max</option>
                      <option value="target">target</option>
                    </select>
                  </div>
                  <button type="button" style={{ ...btnGhostDanger, marginLeft: 'auto' }} onClick={() => removeCrit(i)}>
                    <Trash2 size={14} />
                  </button>
                </div>

                {/* WEIGHT SLIDER */}
                <div style={{ marginTop: 12 }}>
                  <span style={label}>Weight — {c.weight.toFixed(2)}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="range"
                      min={0}
                      max={5}
                      step={0.05}
                      value={c.weight}
                      onChange={(e) => patchCrit(i, { weight: Number(e.target.value) })}
                      style={{ flex: 1, accentColor: 'var(--accent, #4f46e5)' }}
                    />
                    <input
                      type="number"
                      min={0}
                      step={0.05}
                      style={{ ...field, width: 84 }}
                      value={c.weight}
                      onChange={(e) => patchCrit(i, { weight: Number(e.target.value) })}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <span style={label}>Expression (config-eval JSON)</span>
                  <textarea
                    style={{ ...field, width: '100%', height: 84, padding: 10, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                    defaultValue={JSON.stringify(c.expr, null, 2)}
                    onBlur={(e) => {
                      try {
                        patchCrit(i, { expr: JSON.parse(e.target.value) });
                      } catch {
                        /* keep prior */
                      }
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* slots */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ ...label, marginBottom: 0 }}>Slots</span>
            <button type="button" style={{ ...btnAccent, marginLeft: 'auto' }} onClick={addSlot}>
              <Plus size={14} /> Add slot
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rec.slots.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', border: '1px solid var(--line, #e5e7eb)', borderRadius: 9, padding: 10 }}>
                <div>
                  <span style={label}>Slot</span>
                  <select style={field} value={s.slot} onChange={(e) => patchSlot(i, { slot: e.target.value as SpecSlot['slot'] })}>
                    {SLOTS.map((sl) => (
                      <option key={sl} value={sl}>{sl}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <span style={label}>Rank</span>
                  <input type="number" style={{ ...field, width: 80 }} value={s.rank} onChange={(e) => patchSlot(i, { rank: Number(e.target.value) })} />
                </div>
                <button type="button" style={{ ...btnGhostDanger, marginLeft: 'auto' }} onClick={() => removeSlot(i)}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </StudioPanel>
  );
}
